import i18n from '../i18n';

import { clearMpcUnlockCache } from './mpcUnlockCache';
import { setSessionToken } from './sessionContext';

export function backendLang(): string {
  const lng = i18n.language || 'en';
  return lng.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

function withLang(path: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}lang=${backendLang()}`;
}

export type LoginResponse = {
  must_change_password: boolean;
  mfa_required?: boolean;
  mfa_token?: string;
  error?: string;
};
export type MessageResponse = { message?: string; error?: string; ok?: boolean };
export type UserRow = {
  id: number;
  username: string;
  is_admin: boolean;
  must_change_password: boolean;
  banned: boolean;
  mfa_enabled?: boolean;
  created_at: string;
};
export type UsersResponse = { users: UserRow[]; error?: string };

export type UserProfile = {
  username: string;
  has_payment_password: boolean;
  mfa_enabled?: boolean;
  mpc_stub: boolean;
  mpc_enabled?: boolean;
  mpc_server_repo_note?: string;
  mpc_client_repo_note?: string;
  error?: string;
};

export type WalletSummary = {
  id: number;
  name: string;
  source: string;
  evm_address?: string;
  /** When true, wallet is omitted from the home list unless “show hidden” is on. */
  hidden?: boolean;
};

export type WalletsResponse = { wallets: WalletSummary[]; error?: string };

export type WalletDetail = {
  id: number;
  name: string;
  evm_address: string;
  ripple_address?: string;
  xrp_balance?: string;
  ripple_regular_key?: string;
  ripple_disable_master?: boolean;
  source: string;
  hidden?: boolean;
  error?: string;
};

export type CatalogToken = {
  chain: string;
  token_ref: string;
  symbol: string;
  name: string;
  decimals: number;
};

/** Watch list row from GET .../tokens (metadata only). */
export type WatchedTokenMeta = {
  chain: string;
  token_ref: string;
  symbol: string;
  name: string;
  decimals: number;
};

/** One entry from POST .../tokens/balances (includes display fields from on-chain resolution for custom contracts). */
export type WatchedTokenBalanceEntry = {
  chain: string;
  token_ref: string;
  symbol?: string;
  name?: string;
  decimals?: number;
  balance_formatted: string;
  balance_error: string | null;
};

/** Merged row for the UI (optional fields while balance is loading). */
export type WatchedTokenRow = WatchedTokenMeta & {
  balance_formatted: string;
  balance_error: string | null;
  balance_pending?: boolean;
};

export type CatalogResponse = { tokens: CatalogToken[]; error?: string };
export type WatchedTokensMetaResponse = { tokens: WatchedTokenMeta[]; error?: string };
export type WatchedTokenBalancesResponse = { balances: WatchedTokenBalanceEntry[]; error?: string };

export function userWalletApiBase(walletId: number): string {
  return `/api/user/wallet/${walletId}`;
}

async function parseJSON<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return { error: text } as T;
  }
}

export async function postJSON<T>(path: string, body: unknown): Promise<{ res: Response; data: T }> {
  const res = await fetch(withLang(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await parseJSON<T>(res);
  return { res, data };
}

export async function patchJSON<T>(path: string, body: unknown): Promise<{ res: Response; data: T }> {
  const res = await fetch(withLang(path), {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await parseJSON<T>(res);
  return { res, data };
}

export async function getJSON<T>(path: string): Promise<{ res: Response; data: T }> {
  const res = await fetch(withLang(path), { credentials: 'include' });
  const data = await parseJSON<T>(res);
  return { res, data };
}

export async function postLogout(): Promise<void> {
  clearMpcUnlockCache();
  setSessionToken(undefined);
  await fetch(withLang('/api/logout'), { method: 'POST', credentials: 'include' });
}

export type SessionPingResponse = { ok?: boolean; admin?: boolean; session_token?: string; error?: string };

/** Lightweight session refresh (sliding cookie expiry). Safe to call when not logged in (401). */
export async function sessionPing(): Promise<SessionPingResponse> {
  const { res, data } = await getJSON<SessionPingResponse>('/api/session/ping');
  if (!res.ok) {
    setSessionToken(undefined);
    return { ...data, error: data.error ?? 'session' };
  }
  if (data.session_token?.trim()) {
    setSessionToken(data.session_token.trim());
  }
  return data;
}

export type SwapPoolQuote = {
  protocol: string;
  version: string;
  pool_address?: string;
  amount_out_formatted: string;
  amount_out_min_formatted: string;
  liquidity_note: string;
  quote_error: string | null;
};

export type SwapQuoteResponse = {
  wallet_id: number;
  wallet_name: string;
  chain: string;
  gas_price_gwei: string;
  amount_in_formatted: string;
  token_in_symbol: string;
  token_out_symbol: string;
  slippage_bps: number;
  pools: SwapPoolQuote[];
  error?: string;
};

export type WalletSessionResponse = {
  encrypted_client_session: string;
  encrypted_client_secret?: string;
  error?: string;
};

export type TransferNetworkResponse = {
  chain: string;
  gas_price_gwei: string;
  base_fee_gwei: string;
  error?: string;
};

export type TransferPendingNonceResponse = {
  nonce?: number;
  error?: string;
};

/** Unsigned EIP-1559 fields from GET/POST transfer prepare (plus signing_digest_hex). */
export type TransferTransactionInfo = {
  type: number;
  chain_id: string;
  nonce: number;
  gas: number;
  max_priority_fee_per_gas_wei: string;
  max_fee_per_gas_wei: string;
  to: string;
  value_wei: string;
  data: string;
  signing_digest_hex: string;
};

export type TransferPrepareResponse = {
  wallet_id: number;
  wallet_name: string;
  chain: string;
  token_ref: string;
  token_symbol: string;
  amount_formatted: string;
  to_address: string;
  nonce: number;
  gas_limit: number;
  digest_hex: string;
  max_fee_gwei: string;
  priority_fee_gwei: string;
  /** Constructed unsigned transaction (EIP-1559) for display / review. */
  transaction?: TransferTransactionInfo;
  error?: string;
};

export type TransferSubmitResponse = {
  message?: string;
  tx_hash?: string;
  error?: string;
};

export type ProxyTransferResolvedRow = {
  token_ref: string;
  symbol: string;
  decimals: number;
  amount_raw: string;
};

export type ProxyTransferPrepareResponse = {
  wallet_id: number;
  wallet_name?: string;
  chain: string;
  source_address: string;
  to_address?: string;
  proxy_address: string;
  delegation_address: string;
  nonce: number;
  auth_nonce: number;
  gas_limit: number;
  digest_hex: string;
  max_fee_gwei?: string;
  priority_fee_gwei?: string;
  max_fee_per_gas_wei: string;
  max_priority_fee_per_gas_wei: string;
  transaction?: TransferTransactionInfo;
  resolved_transfers?: ProxyTransferResolvedRow[];
  error?: string;
};

export type ProxyTransferSubmitResponse = {
  message?: string;
  tx_hash?: string;
  error?: string;
};

export type ProxyTransferPendingNonceResponse = {
  nonce?: number;
  error?: string;
};

export type RipplePrepareResponse = {
  signing_digest_hex: string;
  tx_json: string;
  error?: string;
};

export type RippleSubmitResponse = {
  message?: string;
  engine_result?: string;
  engine_message?: string;
  tx_hash?: string;
  error?: string;
};

export type SwapExchangePrepareResponse = {
  wallet_id: number;
  wallet_name: string;
  chain: string;
  pool_address: string;
  version: string;
  nonce: number;
  gas_limit: number;
  digest_hex: string;
  max_fee_gwei: string;
  priority_fee_gwei: string;
  transaction?: TransferTransactionInfo;
  error?: string;
};

export type SwapExchangeSubmitResponse = {
  message?: string;
  tx_hash?: string;
  error?: string;
};

export type AdminSwapTokenRow = {
  id: number;
  chain: string;
  /** Stored ref: `native` or 40-char hex (no 0x), matches backend `address`. */
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  enabled: boolean;
  source: string;
  updated_at: string;
};

export type AdminSwapPoolRow = {
  id: number;
  chain: string;
  protocol: string;
  version: string;
  token_in_address: string;
  token_out_address: string;
  fee_tier: number | null;
  pool_address: string;
  factory_address: string;
  router_address: string;
  quoter_address: string;
  sort_order: number;
  enabled: boolean;
  source: string;
  updated_at: string;
};
